import Supercluster from "supercluster";

export const CITY_ZOOM_MAX = 8;
export const QUARTER_ZOOM_MAX = 12;
export const MICRO_ZOOM_MAX = 15;
export const MOSAIC_MAP_PCT = 18;
export const LISTING_FOCUS_ZOOM = 16;

export const listingFocusPoint = (listing) => {
  if (!isMappable(listing)) return null;
  return {
    lat: listing.location.lat,
    lng: listing.location.lng,
    zoom: LISTING_FOCUS_ZOOM,
  };
};

export const isMappable = (listing) => {
  const lat = listing?.location?.lat;
  const lng = listing?.location?.lng;
  return Number.isFinite(lat) && Number.isFinite(lng);
};

export const buildingKey = (listing) => {
  if (!isMappable(listing)) return null;
  return `bldg:${listing.location.lat},${listing.location.lng}`;
};

export const listingInBounds = (listing, bounds) => {
  if (!bounds || !isMappable(listing)) return false;
  const { lat, lng } = listing.location;
  return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
};

export const formatCompactPrice = (price, t) => {
  const amount = price?.amount;
  if (amount == null) return t ? t("priceOnRequest") : "";
  const symbol = price?.currency === "RUB" || !price?.currency ? "₽" : price.currency;
  if (Math.abs(amount) >= 1_000_000) {
    const mln = amount / 1_000_000;
    const num = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(mln);
    return `${num} млн ${symbol}`;
  }
  const num = new Intl.NumberFormat("ru-RU").format(amount).replace(/\s/g, "\u00A0");
  return `${num}\u00A0${symbol}`;
};

const minPriced = (listings) => {
  const priced = listings.filter((it) => it.price?.amount != null);
  if (!priced.length) return listings[0];
  return priced.reduce((a, b) => (a.price.amount <= b.price.amount ? a : b));
};

export const buildingPinLabel = (listings, t) => {
  if (!listings.length) return "";
  const cheapest = minPriced(listings);
  const price = formatCompactPrice(cheapest?.price, t);
  if (listings.length === 1) return price;
  return `${listings.length} > ${price}`;
};

const WORLD = [-180, -90, 180, 90];

const bboxFromBounds = (bounds) => {
  if (!bounds) return WORLD;
  return [bounds.west, bounds.south, bounds.east, bounds.north];
};

const clusterByCity = (listings) => {
  const groups = new Map();
  listings.forEach((it) => {
    const city = it.location?.city || "";
    if (!groups.has(city)) groups.set(city, []);
    groups.get(city).push(it);
  });
  return Array.from(groups.entries()).map(([city, items]) => {
    const lat = items.reduce((s, it) => s + it.location.lat, 0) / items.length;
    const lng = items.reduce((s, it) => s + it.location.lng, 0) / items.length;
    return {
      type: "cluster",
      level: "city",
      key: `city:${city}`,
      lat,
      lng,
      count: items.length,
      listingIds: items.map((it) => it.id),
    };
  });
};

const clusterByBuilding = (listings, t) => {
  const groups = new Map();
  listings.forEach((it) => {
    const key = buildingKey(it);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  });
  return Array.from(groups.entries()).map(([key, items]) => ({
    type: "building",
    level: "building",
    key,
    lat: items[0].location.lat,
    lng: items[0].location.lng,
    count: items.length,
    listingIds: items.map((it) => it.id),
    label: buildingPinLabel(items, t),
  }));
};

const clusterBySupercluster = (listings, zoom, bounds, level, radius) => {
  const byId = new Map(listings.map((it) => [it.id, it]));
  const index = new Supercluster({ radius, maxZoom: 20, minZoom: 0 });
  index.load(listings.map((it) => ({
    type: "Feature",
    properties: { listingId: it.id },
    geometry: { type: "Point", coordinates: [it.location.lng, it.location.lat] },
  })));
  const z = Math.max(0, Math.min(20, Math.floor(zoom)));
  return index.getClusters(bboxFromBounds(bounds), z).map((f, i) => {
    const [lng, lat] = f.geometry.coordinates;
    let listingIds;
    if (f.properties.cluster) {
      listingIds = index.getLeaves(f.properties.cluster_id, Infinity).map((leaf) => leaf.properties.listingId);
    } else {
      listingIds = [f.properties.listingId];
    }
    listingIds = listingIds.filter((id) => byId.has(id));
    return {
      type: "cluster",
      level,
      key: f.properties.cluster ? `sc:${level}:${f.properties.cluster_id}` : `pt:${level}:${listingIds[0] || i}`,
      lat,
      lng,
      count: listingIds.length,
      listingIds,
    };
  });
};

export const getMapMarkers = (listings, zoom, bounds, t) => {
  const mappable = listings.filter(isMappable);
  if (!mappable.length) return [];
  if (zoom <= CITY_ZOOM_MAX) return clusterByCity(mappable);
  if (zoom <= QUARTER_ZOOM_MAX) return clusterBySupercluster(mappable, zoom, bounds, "quarter", 90);
  if (zoom <= MICRO_ZOOM_MAX) return clusterBySupercluster(mappable, zoom, bounds, "micro", 50);
  return clusterByBuilding(mappable, t);
};

export const listingsForDrawer = ({ filtered, bounds, lockedIds, hoveredMapIds }) => {
  let list = filtered.filter(isMappable);
  if (bounds) list = list.filter((it) => listingInBounds(it, bounds));
  if (lockedIds?.length) return list.filter((it) => lockedIds.includes(it.id));
  if (hoveredMapIds?.length) return list.filter((it) => hoveredMapIds.includes(it.id));
  return list;
};
